import { memo, useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react'
import ReactDOM from 'react-dom'
import { trackInternalEvent } from '@rio/tracking'
import Icon24 from 'builder/components/Icon'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { MouseClickEvent } from 'builder/modules/ui'
import { FORMATS } from 'builder/modules/constants'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { usePortalNode } from 'builder/hooks/usePortalNode'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { reflow } from 'builder/utils/reflow'
import {
  Container,
  ActionButton,
  ActionMenuContainer,
  ActionMenuItem,
  IconContainer,
  ButtonContainer,
  ActionButtonWrapper,
  SelectedAction,
  IconUpNDown,
  SelectedActionWrapper,
  MobileOverlay,
  MobileOption,
  MobileOptionsContainer,
  MobileCancelButton,
  Separator,
} from './styles'
import { DownloadButtonProps } from './types'

const USER_PREFERRED_DOWNLOAD_ACTION = 'USER_PREFERRED_DOWNLOAD_ACTION' as const
const LOCALIZATION_KEY = 'builder.resume_editor.document_download'

export const DownloadButtonVersionTwo = memo(
  ({ documentId, documentType, template, onDownload, isPreviewLayout }: DownloadButtonProps) => {
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false)
    const [selectedFormat, setSelectedFormat] = useState<FORMATS>(FORMATS.pdf)
    const { i18n } = useI18n()
    const menuRef = useRef<HTMLDivElement | null>(null)
    const buttonRef = useRef<HTMLDivElement | null>(null)
    const { isPhone } = useMediaQueries()
    const portalNode = usePortalNode()
    const overlayRef = useRef<HTMLDivElement | null>(null)

    type FormatType = typeof FORMATS[keyof typeof FORMATS]

    useEffect(() => {
      const saved = localStorage.getItem(USER_PREFERRED_DOWNLOAD_ACTION)

      const validFormats = Object.values(FORMATS) as FormatType[]
      if (saved && validFormats.includes(saved as FormatType)) {
        setSelectedFormat(saved as FormatType)
      }
    }, [])

    useClickOutsideExcept(menuRef, [buttonRef], () => {
      setIsDownloadMenuOpen(false)
    })

    const toggleMenu = useCallback(() => {
      if (!isDownloadMenuOpen) {
        trackInternalEvent('click_download_resume_formats', {
          source: isPreviewLayout ? 'full_screen_resume_preview' : 'resume_editor',
          template: template,
        })
      }
      setIsDownloadMenuOpen(prev => !prev)
    }, [isDownloadMenuOpen, isPreviewLayout, template])

    const closeMenu = useCallback(() => {
      setIsDownloadMenuOpen(false)
    }, [])

    const handleSelect = useCallback((key: FORMATS) => {
      setSelectedFormat(key)
      localStorage.setItem(USER_PREFERRED_DOWNLOAD_ACTION, key)
      setIsDownloadMenuOpen(false)
    }, [])

    const handleDownloadDocument = useCallback(
      ({ event }: { event: MouseClickEvent }) => {
        onDownload({
          id: documentId,
          type: documentType,
          format: selectedFormat,
          source: 'additional_functionality_button',
          event: event,
        })
      },
      [onDownload, documentId, documentType, selectedFormat],
    )

    useEscapeHandler(closeMenu)

    useLayoutEffect(() => {
      if (overlayRef.current) reflow(overlayRef.current)
      if (menuRef.current) reflow(menuRef.current)
    }, [])

    const DesktopMenu = useMemo(() => {
      return (
        <ActionMenuContainer ref={menuRef} isVisible={isDownloadMenuOpen}>
          <ActionMenuItem onClick={() => handleSelect(FORMATS.pdf)}>
            <IconContainer>
              <Icon24.PdfCurrentColor />
            </IconContainer>
            {i18n.t(`${LOCALIZATION_KEY}.options.pdf`)}
          </ActionMenuItem>
          <ActionMenuItem onClick={() => handleSelect(FORMATS.docx)}>
            <IconContainer>
              <Icon24.Docx />
            </IconContainer>
            {i18n.t(`${LOCALIZATION_KEY}.options.docx`)}
          </ActionMenuItem>
          <ActionMenuItem onClick={() => handleSelect(FORMATS.txt)}>
            <IconContainer>
              <Icon24.Txt />
            </IconContainer>
            {i18n.t(`${LOCALIZATION_KEY}.options.txt`)}
          </ActionMenuItem>
        </ActionMenuContainer>
      )
    }, [handleSelect, i18n, isDownloadMenuOpen])

    const MobileMenu = useMemo(() => {
      return ReactDOM.createPortal(
        <MobileOverlay ref={overlayRef} isVisible={isDownloadMenuOpen}>
          <MobileOptionsContainer ref={menuRef} isVisible={isDownloadMenuOpen}>
            <MobileOption onClick={() => handleSelect(FORMATS.pdf)}>
              <IconContainer>
                <Icon24.PdfCurrentColor />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.pdf`)}
            </MobileOption>
            <Separator />
            <MobileOption onClick={() => handleSelect(FORMATS.docx)}>
              <IconContainer>
                <Icon24.Docx />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.docx`)}
            </MobileOption>
            <Separator />
            <MobileOption onClick={() => handleSelect(FORMATS.txt)}>
              <IconContainer>
                <Icon24.Txt />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.txt`)}
            </MobileOption>
          </MobileOptionsContainer>
          <MobileCancelButton isVisible={isDownloadMenuOpen}>
            {i18n.t('builder.resume_editor.cancel')}
          </MobileCancelButton>
        </MobileOverlay>,
        portalNode,
      )
    }, [handleSelect, i18n, isDownloadMenuOpen, portalNode])

    return (
      <Container>
        <ButtonContainer ref={buttonRef}>
          <ActionButtonWrapper>
            <SelectedActionWrapper>
              <SelectedAction
                isDownloadMenuOpen={isDownloadMenuOpen}
                isPreviewLayout={isPreviewLayout}
                onClick={toggleMenu}
              >
                {i18n.t(`${LOCALIZATION_KEY}.options.${selectedFormat}`)} <IconUpNDown />
              </SelectedAction>
              {isPhone ? MobileMenu : DesktopMenu}
            </SelectedActionWrapper>
            <ActionButton
              size="small"
              onClick={(event: MouseClickEvent) => handleDownloadDocument({ event })}
            >
              {i18n.t(`${LOCALIZATION_KEY}.download_button_text`)}
            </ActionButton>
          </ActionButtonWrapper>
        </ButtonContainer>
      </Container>
    )
  },
)
